import React, { useEffect, useState } from "react";
import { FileAudio, FileText } from "lucide-react";
import { Button } from "./button";

export interface FilePreviewProps {
  fileUrl: string;
  className?: string;
  containerClassName?: string;
  fileType?: string;
}

/**
 * A reusable component for previewing different types of files
 * Supports video, audio, PDF, DOCX, and provides fallback for unknown file types
 */
const FilePreview: React.FC<FilePreviewProps> = ({
  fileUrl,
  className = "",
  containerClassName = "",
  fileType,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState<
    "video" | "audio" | "pdf" | "docx" | "unknown"
  >("unknown");

  useEffect(() => {
    if (fileType) {
      setError(null);

      try {
        // Determine file type based on extension
        if (fileType && fileType.toLowerCase().includes("video")) {
          setType("video");
        } else if (fileType && fileType.toLowerCase().includes("audio")) {
          setType("audio");
        } else if (fileType && fileType.toLowerCase().includes("pdf")) {
          setType("pdf");
        } else if (fileType && fileType.toLowerCase().includes("document")) {
          setType("docx");
        } else {
          setType("unknown");
          setError(`Unsupported file type: ${fileType}`);
        }
      } catch (err) {
        console.error("Error determining file type:", err);
        setError("Error determining file type");
      } finally {
        setLoading(false);
      }
    } else if (fileUrl) {
      try {
        const fileType = fileUrl.split(".").pop(); // Get the file extension

        // Determine file type based on extension
        if (fileType && fileType.toLowerCase().includes("mp4")) {
          setType("video");
        } else if (fileType && fileType.toLowerCase().includes("mp3")) {
          setType("audio");
        } else if (fileType && fileType.toLowerCase().includes("pdf")) {
          setType("pdf");
        } else if (fileType && fileType.toLowerCase().includes("docx")) {
          setType("docx");
        } else {
          setType("unknown");
          setError(`Unsupported file type: ${fileType}`);
        }
      } catch (err) {
        console.error("Error determining file type:", err);
        setError("Error determining file type");
      } finally {
        setLoading(false);
      }
    }
  }, [fileType, fileUrl]);

  // Render different content based on file type
  if (loading) {
    return (
      <div
        className={`flex flex-col items-center justify-center h-[400px] ${containerClassName}`}
      >
        <div className="w-12 h-12 border-4 border-t-[#5C88C5] border-r-[#5C88C5] border-b-[#5C88C5] border-l-transparent rounded-full animate-spin"></div>
        <p className="mt-4 text-gray-600">Loading file...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`flex flex-col items-center justify-center h-[400px] ${containerClassName}`}
      >
        <div className="text-red-500 mb-4">
          <FileText size={48} />
        </div>
        <p className="text-red-500">{error}</p>
        <Button
          onClick={() => window.open(fileUrl, "_blank")}
          className="mt-4"
          variant="primary"
        >
          Download File
        </Button>
      </div>
    );
  }

  switch (type) {
    case "video":
      return (
        <div className={containerClassName}>
          <video
            src={fileUrl}
            controls
            className={`max-w-full max-h-[500px] w-full ${className}`}
            controlsList="nodownload"
          />
        </div>
      );

    case "audio":
      return (
        <div
          className={`flex flex-col items-center justify-center py-10 w-full ${containerClassName}`}
        >
          <div className="text-[#5C88C5] mb-6">
            <FileAudio size={64} />
          </div>
          <audio
            src={fileUrl}
            controls
            className={`w-full max-w-[500px] ${className}`}
            controlsList="nodownload"
          />
        </div>
      );

    case "pdf":
      return (
        // <div className={containerClassName}>
        <iframe
          src={`${fileUrl}`}
          width="100%"
          height="500px"
          title="PDF Viewer"
          className={`border-none ${className}`}
        />
        // </div>
      );

    case "docx":
      return (
        // <div className={containerClassName}>
        <iframe
          src={`https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(
            fileUrl
          )}`}
          width="100%"
          height="500px"
          title="Word Viewer"
          className={`border-none ${className}`}
        />
        // </div>
      );

    default:
      return (
        <div
          className={`flex flex-col items-center justify-center h-[400px] ${containerClassName}`}
        >
          <div className="text-[#5C88C5] mb-6">
            <FileText size={64} />
          </div>
          <p className="mb-4">This file type cannot be previewed.</p>
          <Button
            onClick={() => window.open(fileUrl, "_blank")}
            variant="primary"
          >
            Download File
          </Button>
        </div>
      );
  }
};

export default FilePreview;
